import React, { useEffect, useRef, useState } from 'react';
import { styles } from './MenuDropdown.css';
import { classNames } from '@jbhi-fi/lanyard-ui/global/utils';
import { MenuItem, MenuItemProps } from '../MenuItem/MenuItem';
import { DropdownHeading } from './DropdownHeading/DropdownHeading';
import { useTrackingContext } from '../../Tracking';

const MAX_OVERFLOW_SHADOW_HEIGHT = 25;

export type MenuDropdownProps = {
  label: string;
  menuItems: Array<MenuItemProps>;
  isOpen: boolean;
  onBack?: (e: React.MouseEvent | React.KeyboardEvent) => void;
  className?: string;
  trackingData?: { nesting?: Array<string | undefined> };
};

export const MenuDropdown = ({ menuItems, label, className, isOpen, onBack, trackingData }: MenuDropdownProps) => {
  const ref = useRef<HTMLDivElement>(null);
  const [isOverflowing, setIsOverflowing] = useState(false);
  const [overflowShadowHeight, setOverflowShadowHeight] = useState(MAX_OVERFLOW_SHADOW_HEIGHT);
  const { tracker } = useTrackingContext();

  useEffect(() => {
    if (ref.current) {
      const { current } = ref;
      setIsOverflowing(current.scrollHeight > current.clientHeight);
    }
  }, [ref, isOpen]);

  const handleScroll = () => {
    if (ref.current) {
      const maxScroll = ref.current.scrollHeight - ref.current.clientHeight;
      const scrollGap = maxScroll - ref.current.scrollTop;
      if (scrollGap > MAX_OVERFLOW_SHADOW_HEIGHT) {
        return setOverflowShadowHeight(MAX_OVERFLOW_SHADOW_HEIGHT);
      }
      setOverflowShadowHeight(scrollGap);
    }
  };

  const handleKeyDown = (event: React.KeyboardEvent) => {
    if (!event || event.currentTarget !== ref.current) return;

    if (event.key === 'Escape' && onBack) {
      onBack(event);
    }
  };

  useEffect(() => {
    const current = ref.current;
    if (!current) return;

    current.addEventListener('scroll', handleScroll);
    current.addEventListener('keydown', (event: KeyboardEvent) =>
      handleKeyDown(event as unknown as React.KeyboardEvent)
    );

    return () => {
      current?.removeEventListener('scroll', handleScroll);
      current?.removeEventListener('keydown', (event: KeyboardEvent) =>
        handleKeyDown(event as unknown as React.KeyboardEvent)
      );
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [ref]);

  const handleClick = (event: React.MouseEvent, menuItem: MenuItemProps) => {
    tracker.trackCustomEvent({
      event: `custom.menu.click`,
      group: tracker.group,
      label: menuItem.label,
      type: menuItem?.href ? 'open' : 'expand',
      info: {
        nesting: `${trackingData?.nesting ? `${trackingData?.nesting?.filter(Boolean).join(' | ')} | ` : ''}${
          menuItem.label
        }`,
        ...(menuItem?.href ? { url: menuItem?.href } : {}),
      },
    });
    menuItem.onClick?.(event);
  };

  return (
    <div
      className={classNames([
        styles.container({
          visibility: isOpen ? 'visible' : 'hidden',
        }),
        className,
      ])}
      data-testid="navigation-menu-dropdown"
    >
      <DropdownHeading label={label} onBack={onBack} />
      <div ref={ref} className={styles.menu} data-testid="navigation-menu-dropdown-menu">
        <ul className={styles.list} data-testid="navigation-menu-dropdown-list">
          {menuItems.map((menuItem, i) => (
            <MenuItem
              key={i}
              type={menuItem.type}
              label={menuItem.label}
              onClick={e => handleClick(e, menuItem)}
              href={menuItem.href}
              isActive={menuItem.isActive}
              shouldHideArrow={menuItem.shouldHideArrow}
              count={menuItem.count}
              leftIcon={menuItem.leftIcon}
              isBold={menuItem.isBold}
              subMenuItems={menuItem.subMenuItems}
              onBack={menuItem.onBack}
              trackingData={trackingData}
              shouldOpenInNewWindow={menuItem.shouldOpenInNewWindow}
            />
          ))}
        </ul>
        <div
          className={styles.overflowShadow({ isVisible: isOverflowing })}
          style={{ height: overflowShadowHeight }}
        ></div>
      </div>
    </div>
  );
};
